import type { AreaStatus, Suggestion } from '../../../shared/types';

export interface PlanChoices { area?: string; includeLeadIds?: string[]; skipScan?: boolean }
interface Snapshot { suggestion: Suggestion; exclude: string[]; choices?: PlanChoices }

/** Screen-local snapshots: going back must never spend another planning request. */
export class SuggestionHistory {
  current: Snapshot | null = null;
  previous: Snapshot[] = [];

  commit(suggestion: Suggestion, exclude: string[], intent: 'replace' | 'remove' | 'choices' = 'replace', choices?: PlanChoices): boolean {
    if (this.current && intent === 'replace' && (suggestion.needs_scan || suggestion.stops.length === 0))
      return false;
    const sameStops =
      this.current &&
      JSON.stringify(this.current.suggestion.stops.map((s) => s.place_id)) ===
      JSON.stringify(suggestion.stops.map((s) => s.place_id));
    if (this.current && (!sameStops || JSON.stringify(this.current.choices) !== JSON.stringify(choices) ||
      (choices && JSON.stringify(this.current.exclude) !== JSON.stringify(exclude)))) this.previous.push(this.current);
    this.current = !suggestion.needs_scan && (suggestion.stops.length > 0 || intent !== 'replace' || (!!choices && !!suggestion.also_due.length))
      ? { suggestion, exclude: [...exclude], ...(choices ? {choices: {...choices, includeLeadIds: [...(choices.includeLeadIds ?? [])]}} : {}) } : null;
    return true;
  }

  back() {
    const snapshot = this.previous.pop();
    if (snapshot) this.current = snapshot;
    return snapshot;
  }

  /** Permanent marks invalidate snapshots: never restore a now-excluded place. */
  invalidate() {
    this.current = null;
    this.previous = [];
  }
}

export const unavailableSuggestion = (
  suggestion: Suggestion, areas: readonly AreaStatus[], hasExclusions = true,
): string => {
  const ordinary = suggestion.stops.filter((stop) => !stop.meeting).length;
  if (ordinary === 0)
    return 'Still nothing fits. Go back to the previous suggestion, mark a place, look the area up, or start over.';
  const area = areas.find((a) => a.name === suggestion.area);
  const pool =
    ordinary > 0 && area?.remaining === ordinary
      ? `${ordinary === 1 ? 'This is' : 'These are'} the only ${ordinary} open ${ordinary === 1 ? 'place' : 'places'} in this area's remaining pool. `
      : '';
  return `Keeping your current suggestion. ${pool}No replacement was available${hasExclusions ? ' with these exclusions' : ' for this day'}. Remove one stop, mark a place, look the area up, or start over.`;
};
